import {
    Button,
    Card,
    Classes,
    Dialog,
    FormGroup,
    InputGroup,
    Intent,
    Menu,
    MenuItem,
    NonIdealState,
    Popover,
    Position,
} from "@blueprintjs/core";
import classNames from "classnames";
import { action, IObservableArray, observable } from "mobx";
import { observer } from "mobx-react";
import React from "react";
import { Configuration, LocalUserState } from "../../common";
import { IVirtualBackgroundEffect } from "../../video";
import styles from "./VideoEffectsDropdown.module.css";

export interface VideoEffectsDropdownProps {
    effect: IVirtualBackgroundEffect;
    localUser: LocalUserState;
}

@observer
export default class VideoEffectsDropdown extends React.PureComponent<VideoEffectsDropdownProps> {
    @observable public backgroundImages: IObservableArray<string> = observable([]);
    @observable public customBackgroundImageUrl?: string;

    @action
    private loadAsyncModules = async () => {
        const images = [];
        for (let i = 1; i <= Configuration.NUM_VIRTUAL_BACKGROUNDS; i++) {
            images[i - 1] = import(`../../assets/images/backgrounds/${i}.jpg`).then((m) => m.default);
        }
        this.backgroundImages.replace(await Promise.all(images));
    };

    private handleBackgroundImageLoaded = (event: React.SyntheticEvent) => {
        this.props.effect?.setBackgroundImage(event.target as HTMLImageElement);
    };

    public componentDidMount() {
        this.loadAsyncModules();
    }

    public render() {
        const { localUser } = this.props;

        return (
            <>
                <Popover position={Position.TOP} minimal={true}>
                    <Button text="Video options" minimal={true} rightIcon="chevron-up" />
                    <Menu>
                        <MenuItem
                            text="Virtual Background"
                            icon={localUser.videoEffect === "virtualBackground" ? "tick" : "mugshot"}
                            onClick={this.handleDialogToggle}
                        />
                        <MenuItem
                            text="Disable effects"
                            icon="cross"
                            onClick={this.disableEffects}
                            intent={Intent.DANGER}
                        />
                    </Menu>
                </Popover>
                <Dialog
                    className={classNames(Classes.DARK, styles.virtualBackgroundDialog)}
                    isOpen={localUser.virtualBackground.isDialogOpen}
                    title="Virtual Background"
                    onClose={this.handleDialogToggle}
                >
                    <div className={classNames(Classes.DIALOG_BODY, styles.virtualBackgroundDialogBody)}>
                        <FormGroup className={styles.dialogFormGroup} label="Custom image">
                            <InputGroup
                                placeholder="Enter url..."
                                defaultValue={this.customBackgroundImageUrl}
                                onChange={this.handleCustomBackgroundUrlChange}
                                rightElement={
                                    <Button
                                        minimal={true}
                                        icon="arrow-right"
                                        onClick={this.handleCustomBackgroundUrlConfirm}
                                    />
                                }
                            />
                        </FormGroup>
                        <FormGroup className={styles.dialogFormGroup} label="Image gallery">
                            <div className={styles.backgroundOptions}>{this.renderBackgroundOptions()}</div>
                        </FormGroup>
                    </div>
                </Dialog>
                <img
                    className={styles.backgroundImage}
                    src={localUser.virtualBackground.selectedImage}
                    onLoad={this.handleBackgroundImageLoaded}
                />
            </>
        );
    }

    private renderBackgroundOptions() {
        const { selectedImage } = this.props.localUser.virtualBackground;
        return this.backgroundImages.map((image) => {
            const isSelected = image === selectedImage;
            return (
                <Card
                    className={styles.backgroundOption}
                    interactive={!isSelected}
                    elevation={isSelected ? 2 : 0}
                    onClick={this.getBackgroundSelectHandler(image)}
                    key={image.substr(0, 10)}
                >
                    <div className={styles.fixedAspectRatioContainer}>
                        <img src={image} />
                    </div>
                    {isSelected && <NonIdealState className={styles.selectedOptionOverlay} icon="tick" />}
                </Card>
            );
        });
    }

    @action
    private handleDialogToggle = () => {
        const { virtualBackground } = this.props.localUser;
        virtualBackground.isDialogOpen = !virtualBackground.isDialogOpen;
    };

    @action
    private getBackgroundSelectHandler = (image: string) => () => {
        const { virtualBackground } = this.props.localUser;
        virtualBackground.selectedImage = image;
        virtualBackground.isDialogOpen = false;
        this.props.localUser.videoEffect = "virtualBackground";
    };

    @action
    private disableEffects = () => {
        this.props.localUser.virtualBackground.isDialogOpen = false;
        this.props.localUser.videoEffect = undefined;
    };

    @action
    private handleCustomBackgroundUrlChange = (event: React.FormEvent<HTMLInputElement>) => {
        this.customBackgroundImageUrl = event.currentTarget.value;
    };

    @action
    private handleCustomBackgroundUrlConfirm = () => {
        if (this.customBackgroundImageUrl !== undefined && this.customBackgroundImageUrl.trim() !== "") {
            this.getBackgroundSelectHandler(this.customBackgroundImageUrl.trim())();
        }
    };
}
